import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navbar from '../components/Navbar';
import { productSlug, productUrl } from './ProductPage';
import Footer from '../components/Footer';
import { ChevronLeft, ChevronRight, Search, ArrowRight, Package, Store, Lock, FileText, Check } from 'lucide-react';
import { useQuoteBasket, useAuth, useAuthDialog } from '../App';
import { QuoteRFQModal } from '../components/QuoteRFQModal';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Brand accent colors (slug → tailwind/hex) ─────────────────────────── */
const BRAND_PALETTE = {
  'abdos':               { from: '#1e3a8a', to: '#3b82f6', light: '#eff6ff' },
  'abcam':               { from: '#5b21b6', to: '#8b5cf6', light: '#f5f3ff' },
  'ansell':              { from: '#991b1b', to: '#ef4444', light: '#fff1f2' },
  'lobachemie':          { from: '#065f46', to: '#10b981', light: '#ecfdf5' },
  'oxford-lab':          { from: '#92400e', to: '#f59e0b', light: '#fffbeb' },
  'macherey-nagel':      { from: '#0c4a6e', to: '#0ea5e9', light: '#f0f9ff' },
  'brand':               { from: '#1e293b', to: '#64748b', light: '#f8fafc' },
  'mp-bio':              { from: '#7c3aed', to: '#c084fc', light: '#faf5ff' },
  'borosilscientific':   { from: '#166534', to: '#22c55e', light: '#f0fdf4' },
  'tm-media':            { from: '#9a3412', to: '#f97316', light: '#fff7ed' },
};

const DEFAULT_PALETTE = { from: '#1e40af', to: '#6366f1', light: '#eef2ff' };

/* ─── Known brand logos — these are proxied to avoid CORS/ORB blocks ─────── */
const RAW_KNOWN_LOGOS = {
  'abdos':            'https://www.abdoslifesciences.com/wp-content/uploads/abdos-logo.png',
  'abcam':            'https://www.abcam.com/ps/cms/images/abcam_logo_2017.png',
  'ansell':           'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Ansell_logo.svg/320px-Ansell_logo.svg.png',
  'macherey-nagel':   'https://www.mn-net.com/media/image/17/94/8c/mn_logo.png',
  'mp-biomedicals':   'https://static.prod-images.emergentagent.com/jobs/a3794646-f60a-4869-92db-2507d04c7985/images/b97eb81a448f130920639035878d376f9e1542bb6790ddec647d97bfc3df8064.png',
  'tarsons':          'https://static.prod-images.emergentagent.com/jobs/a3794646-f60a-4869-92db-2507d04c7985/images/7281b5d889fbf0a0b0082f69841803822d76cd1425f06ce33f3082d84cd14d4c.png',
};

// Proxy all known logo URLs through the backend to avoid browser ORB blocks
const KNOWN_LOGOS = Object.fromEntries(
  Object.entries(RAW_KNOWN_LOGOS).map(([slug, url]) => [
    slug,
    `${API}/api/proxy-image?url=${encodeURIComponent(url)}`,
  ])
);

function getBrandPalette(slug) {
  return BRAND_PALETTE[slug] || DEFAULT_PALETTE;
}

/* ─── Product image: try direct → proxy → brand logo → gradient ─────── */
function ProductImage({ src, name, palette, catalog, brandLogoUrl }) {
  const initStage = src ? 'direct' : (brandLogoUrl ? 'brand' : 'fallback');
  const [stage, setStage] = useState(initStage);
  const proxied = src ? `${API}/api/proxy-image?url=${encodeURIComponent(src)}` : null;

  const activeSrc =
    stage === 'direct' ? src :
    stage === 'proxy'  ? proxied :
    stage === 'brand'  ? brandLogoUrl :
    null;

  const handleError = () => {
    if (stage === 'direct' && proxied)     { setStage('proxy');    return; }
    if (stage === 'proxy'  && brandLogoUrl){ setStage('brand');    return; }
    if (stage === 'brand')                 { setStage('fallback'); return; }
    setStage('fallback');
  };

  if (activeSrc) {
    return (
      <img
        src={activeSrc}
        alt={name}
        loading="lazy"
        decoding="async"
        onError={handleError}
        className="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
      />
    );
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-3">
      <div className="text-3xl font-black opacity-20 text-white select-none">
        {(name || '').slice(0, 2).toUpperCase()}
      </div>
      {catalog && (
        <div className="text-[10px] text-white/50 mt-1 font-mono">{catalog}</div>
      )}
    </div>
  );
}

/* ─── Product card in carousel ────────────────────────────────────────────── */
function ProductCard({ product, palette, brandLogoUrl, onSelect }) {
  const bLogoUrl = brandLogoUrl || null;
  return (
    <div
      onClick={() => onSelect(product)}
      className="group flex-shrink-0 w-44 rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
      data-testid={`product-card-${product.catalog_number || product.id}`}
    >
      {/* Image area with radial gradient BG */}
      <div
        className="relative h-40 overflow-hidden"
        style={{
          background: `radial-gradient(circle at 50% 60%, ${palette.to}33 0%, ${palette.from}cc 100%)`,
        }}
      >
        <ProductImage
          src={product.image_url}
          name={product.name}
          palette={palette}
          catalog={product.catalog_number}
          brandLogoUrl={bLogoUrl}
        />
        {/* View detail hover overlay */}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <span className="px-3 py-1.5 bg-white text-gray-900 text-xs font-bold rounded-full">
            View Details
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <div className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">
          {product.name}
        </div>
        {product.catalog_number && (
          <div className="text-[10px] text-gray-400 font-mono truncate">
            {product.catalog_number}
          </div>
        )}
        {product.category && (
          <div className="text-[10px] text-gray-400 truncate mt-0.5">{product.category}</div>
        )}
      </div>
    </div>
  );
}

/* ─── Horizontal carousel with scroll arrows ─────────────────────────────── */
function ProductCarousel({ products, palette, brandLogoUrl, onSelect }) {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 740, behavior: 'smooth' });
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="flex items-center gap-3 py-4 px-2">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="flex-shrink-0 w-44 h-56 bg-gray-100 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="relative group/carousel">
      {/* Left arrow */}
      <button
        onClick={() => scroll(-1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-gray-50 border border-gray-200"
      >
        <ChevronLeft className="w-4 h-4 text-gray-600" />
      </button>

      {/* Scrollable area */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((p, i) => (
          <ProductCard key={p.id || i} product={p} palette={palette} brandLogoUrl={brandLogoUrl} onSelect={onSelect} />
        ))}
      </div>

      {/* Right arrow */}
      <button
        onClick={() => scroll(1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-gray-50 border border-gray-200"
      >
        <ChevronRight className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );
}

/* ─── Brand section — loads products only when scrolled into view ─────────── */
function BrandSection({ brand, index, navigate }) {
  const [products, setProducts]     = useState([]);
  const [total, setTotal]           = useState(brand.product_count || 0);
  const [loaded, setLoaded]         = useState(false);
  const [fetchStarted, setFetchStarted] = useState(false);
  const sectionRef                  = useRef(null);
  const palette                     = getBrandPalette(brand.slug);
  const logoUrl                     = brand.logo_url || KNOWN_LOGOS[brand.slug];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    // Load products only when section is about to enter the viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fetchStarted) {
          setFetchStarted(true);
          fetch(`${API}/api/brand-products?brand_name=${encodeURIComponent(brand.name)}&limit=8`)
            .then(r => r.json())
            .then(d => { setProducts(d.products || []); setTotal(d.total || 0); setLoaded(true); })
            .catch(() => setLoaded(true));
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }   // start fetching 300 px before section enters view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [brand.name, fetchStarted]);

  return (
    <section
      ref={sectionRef}
      className="border-b border-gray-100 last:border-b-0"
      data-testid={`brand-section-${brand.slug}`}
      style={{ backgroundColor: index % 2 === 0 ? '#fff' : palette.light }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-8">
        {/* Brand header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-4">
            {/* Logo / initials */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200 shadow-sm bg-white"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={brand.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain p-2"
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <span
                className="text-gray-700 font-black text-lg"
                style={{ display: logoUrl ? 'none' : 'flex' }}
              >
                {(brand.name || '').slice(0, 2).toUpperCase()}
              </span>
            </div>

            {/* Name + meta */}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">{brand.name}</h2>
                <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
                  Partner
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Package className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {loaded ? total.toLocaleString() : brand.product_count?.toLocaleString() || '—'} products
                </span>
                {brand.description && (
                  <>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400 line-clamp-1 max-w-xs">{brand.description}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Browse all */}
          <button
            onClick={() => navigate(`/brands/${brand.slug}`)}
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            data-testid={`browse-brand-${brand.slug}`}
          >
            Browse all <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Product carousel */}
        <ProductCarousel
          products={loaded ? products : []}
          palette={palette}
          brandLogoUrl={logoUrl || ''}
          onSelect={(p) => navigate(productUrl(p))}
        />

        {/* Mobile browse all */}
        <button
          onClick={() => navigate(`/brands/${brand.slug}`)}
          className="sm:hidden mt-3 flex items-center gap-1 text-sm font-semibold text-blue-600"
          data-testid={`browse-brand-mobile-${brand.slug}`}
        >
          Browse all {brand.name} products <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────────── */
export default function BrandsPage() {
  const navigate                      = useNavigate();
  const location                      = useLocation();

  // Clear search when user clicks "Brand Store" in the nav while already on this page
  useEffect(() => {
    if (location.state?.clearSearch) setSearch('');
  }, [location.state?.clearSearch]);
  const { quoteItems, addToQuote, removeFromQuote, clearQuote } = useQuoteBasket();
  const { user } = useAuth();
  const { openLabAuth } = useAuthDialog();
  const isLoggedIn = !!user;

  const [brands, setBrands]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [showRFQ, setShowRFQ] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/brands?partner_only=true`)
      .then(r => r.json())
      .then(d => {
        const raw = d.brands || [];
        const enriched = raw.map(b => ({
          ...b,
          slug: b.slug || b.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        }));
        setBrands(enriched);
      })
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = brands.filter(b =>
    !search || b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen" style={{background:'linear-gradient(160deg, #f0f9ff 0%, #f5f3ff 100%)'}}>
      <Helmet>
        <link rel="canonical" href="https://biomall.ai/brands" />
        <title>Shop by Brand | Cell Culture Media &amp; Lab Product Suppliers — BioMall.ai</title>
        <meta name="description" content="Browse verified global brands for cell culture media, chemicals, reagents and lab equipment. Request quotes directly on BioMall.ai." />
        <meta property="og:image" content="https://biomall.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy Life Science Products from Top Brands Online | Request Quote Worldwide — BioMall.ai" />
        <meta name="twitter:description" content="Buy life science products from 1.4M+ SKUs across top partner brands worldwide. Request quotes from verified distributors instantly on BioMall.ai." />
        <meta name="twitter:image" content="https://biomall.ai/og-image.png" />
      </Helmet>
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "BioMall.ai", "item": "https://biomall.ai" },
          { "@type": "ListItem", "position": 2, "name": "Brand Store", "item": "https://biomall.ai/brands" }
        ]
      }} />
      {brands.length > 0 && <JsonLd data={{
        "@context": "https://schema.org", "@type": "ItemList",
        "name": "Life Science Partner Brands on BioMall.ai",
        "description": "Verified partner brands with full product catalogues available for enquiry on BioMall.ai.",
        "itemListElement": brands.map((b, i) => ({
          "@type": "ListItem", "position": i + 1, "name": b.name,
          "url": `https://biomall.ai/brands/${b.slug || b.name.toLowerCase().replace(/\s+/g, '-')}`
        }))
      }} />}
      <Navbar
        searchPlaceholder="Search products, buyers, offers…"
        onSubmit={q => navigate(`/?q=${encodeURIComponent(q)}`)}
      />

      {/* ── Page hero ────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden" style={{background:'#2563eb'}}>
          {/* Tiled lab equipment pattern */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/30a0c147-fa16-45f9-b97f-8fe64a372c9d/images/ca63ae77a721c9fda68725fc58f9d1484e2e200e37030a45882b56d67306d4ff.png)',
            backgroundSize: '340px',
            backgroundRepeat: 'repeat',
          }} />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-8 py-10">
            <h1 className="text-3xl sm:text-4xl font-black mb-2 tracking-tight text-white flex items-center gap-3">
              <Store className="w-8 h-8 text-blue-200" />
              Brand Store
            </h1>
            <p className="text-blue-100 text-sm mb-3">
              {loading
                ? 'Loading brands...'
                : `${brands.length} verified partner brand${brands.length !== 1 ? 's' : ''} · Full catalogues available for enquiry`}
            </p>
            <p className="text-blue-200 text-xs mb-5 flex items-center gap-1.5" data-testid="brands-fulfillment-note">
              <span>&#9432;</span> Get quotations directly from BioMall or from the manufacturer
            </p>
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter brands..."
                className="w-full pl-11 pr-4 py-3 bg-white border-2 border-blue-300 rounded-2xl text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-white transition-all shadow-sm"
                data-testid="brands-filter-input"
              />
            </div>
          </div>
        </div>

      {/* ── Brand carousels ───────────────────────────────────────────────────── */}
      <main>
        {loading ? (
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-8 space-y-8">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 bg-gray-200 rounded-2xl" />
                  <div>
                    <div className="h-5 w-32 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-20 bg-gray-100 rounded" />
                  </div>
                </div>
                <div className="flex gap-3">
                  {[1,2,3,4,5,6].map(j => (
                    <div key={j} className="flex-shrink-0 w-44 h-56 bg-gray-100 rounded-2xl" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            {brands.length === 0 ? (
              <>
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <Package className="w-7 h-7 text-gray-300" />
                </div>
                <p className="text-gray-500 font-semibold">No partner brands yet</p>
                <p className="text-gray-400 text-sm mt-1">Admins can mark brands as partner from the admin dashboard.</p>
              </>
            ) : (
              <p className="text-gray-500">No brands match "{search}"</p>
            )}
          </div>
        ) : (
          filtered.map((brand, i) => (
            <BrandSection
              key={brand.slug}
              brand={brand}
              index={i}
              navigate={navigate}
            />
          ))
        )}
      </main>

      <Footer />

      {/* Floating quote basket bar */}
      {quoteItems.length > 0 && !showRFQ && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowRFQ(true)}
            className="flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-xl transition-colors font-bold text-sm"
            data-testid="view-quote-basket-btn"
          >
            <span className="w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-xs font-black">
              {quoteItems.length}
            </span>
            Add to Quote
          </button>
        </div>
      )}

      {/* Multi-product RFQ modal */}
      {showRFQ && (
        <QuoteRFQModal
          brand={null}
          items={quoteItems}
          onRemoveItem={removeFromQuote}
          onSubmitDone={clearQuote}
          onClose={() => setShowRFQ(false)}
        />
      )}
    </div>
  );
}
